import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Divider,
  Typography,
  Alert,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { 
  Subscription,
  SubscriptionPatchRequest,
  SubscriptionStatus,
  ResumeStrategy,
} from '@/api/bol-notifications';
import { PrimaryButton, SecondaryButton } from '@/components/index';

export interface SubscriptionEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: SubscriptionPatchRequest) => Promise<void>;
  subscription: Subscription;
}

interface FormData {
  name: string;
  status: SubscriptionStatus;
  resumeStrategy?: ResumeStrategy;
  resumeTimestamp?: Date | null;
}

export const SubscriptionEditDialog: React.FC<SubscriptionEditDialogProps> = ({
  open,
  onClose,
  onSubmit,
  subscription,
}) => {
  const form = useForm<FormData>({
    defaultValues: {
      name: '',
      status: SubscriptionStatus.Active,
      resumeStrategy: undefined,
      resumeTimestamp: null,
    }
  });

  const { isSubmitting } = form.formState;
  const watchedResumeStrategy = useWatch({
    control: form.control,
    name: 'resumeStrategy',
  });

  const watchedStatus = useWatch({
    control: form.control,
    name: 'status',
  });

  useEffect(() => {
    if (watchedStatus === SubscriptionStatus.Disabled) {
      form.setValue('resumeStrategy', undefined);
      form.setValue('resumeTimestamp', null);
    }
  }, [watchedStatus, form]);

  useEffect(() => {
    if (open && subscription) {
      form.setValue('name', subscription.name || '');
      form.setValue('status', subscription.status || SubscriptionStatus.Active);
      form.setValue('resumeStrategy', undefined);
      form.setValue('resumeTimestamp', null);
    }
  }, [open, subscription, form]);

  const onFormSubmit = async (data: FormData) => {
    try {
      const editData: SubscriptionPatchRequest = {
        name: data.name,
        status: data.status,
      };

      // Include resume strategy and timestamp only when status is ACTIVE
      if (data.status === SubscriptionStatus.Active && data.resumeStrategy) {
        editData.resumeStrategy = data.resumeStrategy;
      }
      if (data.status === SubscriptionStatus.Active && data.resumeStrategy === ResumeStrategy.FromTimestamp && data.resumeTimestamp) {
        editData.resumeTimestamp = data.resumeTimestamp;
      }

      await onSubmit(editData);
      handleClose();
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Subscription</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }} component="form" onSubmit={form.handleSubmit(onFormSubmit)}>
          {/* Display read-only info about the subscription */}
          {subscription && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Subscription ID</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>{subscription.id}</Typography>
              <Typography variant="caption" color="text.secondary">Member</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>{subscription.memberIdentifier}</Typography>
              <Typography variant="caption" color="text.secondary">Notification Type</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>{subscription.notificationType}</Typography>
              <Typography variant="caption" color="text.secondary">Resource Type</Typography>
              <Typography variant="body2">{subscription.resourceType}</Typography>
            </Box>
          )}

          <Controller
            name="name"
            control={form.control}
            rules={{ 
              required: 'Subscription name is required',
              validate: (value) => value.trim() !== '' || 'Subscription name cannot be empty'
            }}
            render={({ field }) => (
              <TextField
                {...field}
                autoFocus
                margin="dense"
                label="Subscription Name"
                fullWidth
                variant="outlined"
                required
                error={!!form.formState.errors.name}
                helperText={form.formState.errors.name?.message || "Enter a descriptive name for the subscription"}
                sx={{ mb: 1 }}
              />
            )}
          />

          <Divider sx={{ my: 2 }} />

          <Controller
            name="status"
            control={form.control}
            render={({ field }) => (
              <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                <InputLabel>Status</InputLabel>
                <Select {...field} label="Status">
                  <MenuItem value={SubscriptionStatus.Active}>Active</MenuItem>
                  <MenuItem value={SubscriptionStatus.Disabled}>Disabled</MenuItem>
                </Select>
              </FormControl>
            )}
          />

          {/* Resume options for adjusting consumption offset - only show when status is ACTIVE */}
          {watchedStatus === SubscriptionStatus.Active && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                Resume Strategy (Optional)
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Use these options to adjust the notification consumption offset. This is useful when re-enabling a disabled subscription or to replay/skip messages.
              </Alert>

              <Controller
                name="resumeStrategy"
                control={form.control}
                render={({ field }) => (
                  <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                    <InputLabel>Resume Strategy</InputLabel>
                    <Select {...field} label="Resume Strategy" value={field.value || ''}>
                      <MenuItem value="">
                        <em>No change (keep current offset)</em>
                      </MenuItem>
                      <MenuItem value={ResumeStrategy.Continue}>Continue - Resume from last committed offset</MenuItem>
                      <MenuItem value={ResumeStrategy.Latest}>Latest - Skip pending messages, start from now</MenuItem>
                      <MenuItem value={ResumeStrategy.Earliest}>Earliest - Resume from beginning of retention period</MenuItem>
                      <MenuItem value={ResumeStrategy.FromTimestamp}>From Timestamp - Resume from a specific time</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              {watchedResumeStrategy === ResumeStrategy.FromTimestamp && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Controller
                    name="resumeTimestamp"
                    control={form.control}
                    rules={{
                      required: watchedResumeStrategy === ResumeStrategy.FromTimestamp ? 'Timestamp is required for FROM_TIMESTAMP strategy' : false
                    }}
                    render={({ field }) => (
                      <DateTimePicker
                        label="Resume From Timestamp *"
                        value={field.value || null}
                        onChange={(newValue) => field.onChange(newValue)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            margin: 'dense',
                            error: !!form.formState.errors.resumeTimestamp,
                            helperText: form.formState.errors.resumeTimestamp?.message || "Select the timestamp from which to resume consumption",
                            sx: { mb: 2 }
                          }
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={handleClose} disabled={isSubmitting}>Cancel</SecondaryButton>
        <PrimaryButton onClick={form.handleSubmit(onFormSubmit)} type="submit" disabled={isSubmitting}>
          Update
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};
